<template>
  <UiModal :modelValue="show" @update:modelValue="handleClose" size="xl" :showFooter="true">
    <template #title>데이터셋 상세 - {{ year }}년 {{ month }}월</template>

    <!-- 기본 슬롯 사용 -->
    <div class="dataset-detail-container">
      <!-- 로딩 상태 -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>데이터를 불러오는 중...</p>
      </div>

      <!-- 데이터 테이블 -->
      <div v-else-if="datasetList.length > 0" class="table-wrapper">
        <UiTable
          :modelValue="datasetList"
          scrollable
          max-height="600px"
          :empty-title="'데이터가 없습니다'"
          :empty-description="'해당 년월의 데이터셋이 존재하지 않습니다'"
        >
          <template #header>
            <tr>
              <th class="sticky-col">조직</th>
              <th class="sticky-col-2">이름</th>
              <!-- 동적 인덱스 헤더 -->
              <th v-for="index in indexHeaders" :key="index.indexId">
                {{ index.indexNm }}<br />(원시/환산)
              </th>
            </tr>
          </template>

          <template #body>
            <tr v-for="(item, idx) in datasetList" :key="idx">
              <td class="sticky-col">{{ item.deptFNm }}</td>
              <td class="sticky-col-2">{{ item.userNm }}</td>
              <!-- 동적 인덱스 값 (원시 / 환산) -->
              <td v-for="index in indexHeaders" :key="index.indexId" class="text-right">
                {{ formatIndexPairValue(item.indexRawValues[index.indexId], item.indexPercentValues[index.indexId]) }}
              </td>
            </tr>
          </template>
        </UiTable>
      </div>

      <!-- 빈 상태 -->
      <div v-else class="empty-state">
        <p class="empty-title">데이터가 없습니다</p>
        <p class="empty-description">해당 년월의 데이터셋이 존재하지 않습니다</p>
      </div>
    </div>

    <template #footerActions>
      <UiButton variant="secondary" @click="() => handleClose(false)">닫기</UiButton>
    </template>
  </UiModal>
</template>

<script setup>
import { ref, watch } from 'vue';
import { usePaDatasetApi } from '~/composables/pa/usePaDatasetApi';

// Props
const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  year: {
    type: String,
    required: true,
  },
  month: {
    type: String,
    required: true,
  },
});

// Emits
const emit = defineEmits(['close']);

// API
const $api = usePaDatasetApi();

// 상태
const loading = ref(false);
const datasetList = ref([]);
const indexHeaders = ref([]);

/**
 * 데이터 조회
 */
const fetchData = async () => {
  if (!props.show || !props.year || !props.month) {
    return;
  }

  loading.value = true;

  try {
    /**
     * API 응답 포맷:
     * {
     *   indexHeaders: [
     *     { indexId: 'E1', indexNm: '인덱스1' },
     *     { indexId: 'E2', indexNm: '인덱스2' },
     *     ...
     *   ],
     *   datasetList: [
     *     {
     *       userId: 'user001',
     *       userNm: '홍길동',
     *       indexRawValues: { E1: 85.5, E2: 90.2, ... },
     *       indexPercentValues: { E1: 12.3, E2: 88.1, ... }
     *     },
     *     ...
     *   ]
     * }
     */
    const response = await $api.fetchDatasetDetail(props.year, props.month);

    if (response && response.datasetList) {
      // 인덱스 헤더 추출
      if (response.datasetList.length > 0) {
        indexHeaders.value = response.indexHeaders || [];
      }

      // 데이터 매핑
      datasetList.value = response.datasetList.map(item => ({
        deptFNm: item.deptFNm,
        userId: item.userId,
        userNm: item.userNm,
        indexRawValues: item.indexRawValues || {},
        indexPercentValues: item.indexPercentValues || {},
      }));
    } else {
      datasetList.value = [];
      indexHeaders.value = [];
    }
  } catch (error) {
    console.error('데이터셋 조회 실패:', error);
    datasetList.value = [];
    indexHeaders.value = [];
  } finally {
    loading.value = false;
  }
};

/**
 * 인덱스 값 포맷팅
 */
const formatIndexValue = value => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  if (typeof value === 'number') {
    return value.toFixed(2);
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? value : parsed.toFixed(2);
};

/**
 * 원시/환산 값을 함께 표시
 */
const formatIndexPairValue = (rawValue, percentValue) => {
  const rawText = formatIndexValue(rawValue);
  const percentText = formatIndexValue(percentValue);
  return `${rawText} / ${percentText}`;
};

/**
 * 닫기 핸들러
 */
const handleClose = value => {
  // UiModal의 update:modelValue 이벤트는 boolean 값을 전달
  // false일 때만 close 이벤트 발생
  if (value === false) {
    emit('close');
  }
};

// show가 true로 변경될 때 데이터 조회
watch(
  () => props.show,
  newVal => {
    if (newVal) {
      fetchData();
    }
  },
  { immediate: true },
);
</script>

<style scoped lang="scss">
.dataset-detail-container {
  min-height: 400px;
  padding: 20px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 15px;

  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f4f6;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  p {
    color: #6b7280;
    font-size: 14px;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.table-wrapper {
  overflow-x: auto;

  :deep(table) {
    min-width: 1200px;

    th,
    td {
      white-space: nowrap;
      padding: 12px 16px;
    }

    .sticky-col {
      position: sticky;
      left: 0;
      background: #f9fafb;
      z-index: 2;
      border-right: 2px solid #e5e7eb;
      font-weight: 600;
    }

    .sticky-col-2 {
      position: sticky;
      left: 120px; /* sticky-col의 너비에 맞춰 조정 */
      background: #f9fafb;
      z-index: 2;
      border-right: 2px solid #e5e7eb;
      font-weight: 600;
    }

    thead th.sticky-col,
    thead th.sticky-col-2 {
      background: #374151;
      color: white;
      z-index: 3;
    }
  }
}

.text-right {
  text-align: right;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 10px;

  .empty-title {
    font-size: 18px;
    font-weight: 600;
    color: #374151;
  }

  .empty-description {
    font-size: 14px;
    color: #6b7280;
  }
}
</style>
